import { Play, Check } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

// PwC "Alliances" content block — title + paragraphs left, large image right with play overlay.
// Plus a quoted testimonial below with orange left bar + avatar.
export default function AlliancesBlock({ title, paragraphs = [], bullets, list, image, quote, quoteAuthor, quoteRole, quoteCompany, quoteAvatar }) {
  return (
    <section className="bg-white py-20 md:py-28" data-testid="alliances-block">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <ScrollReveal>
            <h2 className="serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight mb-10">{title}</h2>
            {paragraphs.length > 0 && (
              <div className="space-y-7 text-[16.5px] leading-[1.7] text-[var(--abs-ink)] max-w-md">
                {paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}
            {Array.isArray(bullets) && bullets.length > 0 && (
              <ul className="mt-2 space-y-4 text-[16.5px] leading-[1.55] text-[var(--abs-ink)] max-w-md">
                {bullets.map((b, i) => (
                  <li key={i} className="flex gap-3">
                    <Check className="mt-1 h-5 w-5 shrink-0 text-[var(--abs-orange)]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {Array.isArray(list) && list.length > 0 && (
              <div className="mt-2 space-y-6 text-[16.5px] leading-[1.6] text-[var(--abs-ink)] max-w-md">
                {list.map((it, i) => (
                  <div key={i}>
                    <div className="font-medium">{it.heading}</div>
                    {it.body && <div className="mt-1 text-[var(--abs-mute)]">{it.body}</div>}
                  </div>
                ))}
              </div>
            )}
          </ScrollReveal>
        </div>
        <ScrollReveal delay={100} className="lg:col-span-7">
          <div className="relative img-frame aspect-[4/3] bg-[var(--abs-soft)]">
            <img src={image} alt={title} className="w-full h-full object-cover" />
            <div className="absolute right-6 bottom-6 border border-white/80 text-white px-3 py-3 group cursor-pointer hover:bg-white hover:text-[var(--abs-ink)] transition-colors">
              <Play className="h-5 w-5" fill="currentColor" />
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* Quote */}
      {quote && (
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 mt-20 lg:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <ScrollReveal className="lg:col-span-4">
            <div className="flex items-start gap-4">
              {quoteAvatar && <img src={quoteAvatar} alt={quoteAuthor} className="w-16 h-16 rounded-full object-cover" />}
              <div className="text-[14.5px] leading-relaxed">
                <div className="font-medium">{quoteAuthor}</div>
                <div className="text-[var(--abs-mute)] mt-1">{quoteRole}</div>
                <div className="text-[var(--abs-mute)]">{quoteCompany}</div>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={80} className="lg:col-span-8 border-l-2 border-[var(--abs-orange)] pl-6 md:pl-8">
            <p className="serif text-[24px] md:text-[30px] lg:text-[34px] leading-[1.35] text-[var(--abs-ink)]">
              "{quote}"
            </p>
          </ScrollReveal>
        </div>
      )}
    </section>
  );
}
